import { createContext, Dispatch, FC, PropsWithChildren, SetStateAction, useEffect, useState } from 'react';
import mixpanel from 'mixpanel-browser';
import * as Sentry from '@sentry/browser';
import { ArcadeAuth, AUTH_STORAGE_KEY } from 'ui/types';
import { AppContext } from './MPAppContextProvider';
import { useContext } from 'react';
export interface LocalAuthProps {
  get: () => ArcadeAuth;
  set: (token: ArcadeAuth) => void;
  clear: () => void;
}
export interface AuthContextProps {
  auth: ArcadeAuth;
  setAuth: Dispatch<SetStateAction<ArcadeAuth>>;
  localAuth: LocalAuthProps;
  logout: () => void;
}
export const getLocalAuth = (): LocalAuthProps => {
  return {
    get: () => {
      const strAuth = window.localStorage.getItem(AUTH_STORAGE_KEY) || '{}';
      return JSON.parse(strAuth);
    },
    set: (auth: ArcadeAuth) => {
      const strAuth = JSON.stringify(auth) || '{}';
      window.localStorage.setItem(AUTH_STORAGE_KEY, strAuth);
    },
    clear: () => {
      window.localStorage.removeItem(AUTH_STORAGE_KEY);
    }
  };
};
export const AuthContext = createContext({} as AuthContextProps);
export const MPAuthProvider: FC<PropsWithChildren> = ({
  children
}) => {
  const localAuth = getLocalAuth();
  const loadAuth = localAuth.get();
  const [auth, setAuthState] = useState(loadAuth);
  const {
    identifyUserForAppcues
  } = useContext(AppContext);

  // Call identify user on all pages to track
  if (auth?.id && window?.Appcues) {
    identifyUserForAppcues(auth);
  }
  useEffect(() => {
    Sentry.setUser({
      id: auth.id,
      email: auth.email
    });
    if (mixpanel.get_distinct_id && auth?.id) {
      mixpanel.identify(auth.id);
      mixpanel.people.set({
        $email: auth.email,
        $name: auth.fullName,
        $role: auth.role,
        $created: auth.createdAt,
        $entityId: auth.entityId,
        $entityName: auth.entityName
      });
    }
  }, [auth.token]);

  // Keep in-memory auth in sync with cross-tab writes to AUTH_STORAGE_KEY.
  // Without this, fields like endpoint/isTeams/role/profile mutate in storage
  // (and are read directly by the API client) while React state stays stale.
  useEffect(() => {
    const onAuthStorage = (e: StorageEvent) => {
      if (e.key !== AUTH_STORAGE_KEY) return;
      if (e.newValue === null) {
        setAuthState({} as ArcadeAuth);
        return;
      }
      try {
        setAuthState(JSON.parse(e.newValue) as ArcadeAuth);
      } catch {
        // Ignore malformed payloads — the next write will reconcile.
      }
    };
    window.addEventListener('storage', onAuthStorage);
    return () => window.removeEventListener('storage', onAuthStorage);
  }, []);
  const setAuth = (auth: ArcadeAuth): void => {
    setAuthState(auth);
    if (auth.token) {
      localAuth?.set?.(auth);
    } else {
      localAuth?.clear?.();
    }
  };
  const logout = (): void => {
    setAuth({} as ArcadeAuth);
    sessionStorage.removeItem('assumedUser');
    Sentry.setUser(null);
    window.location.pathname = '/login';
  };
  return <AuthContext.Provider value={{
    auth,
    setAuth,
    logout,
    localAuth
  }} data-sentry-element="unknown" data-sentry-component="MPAuthProvider" data-sentry-source-file="MPAuthContextProvider.tsx">
      {children}
    </AuthContext.Provider>;
};
export default MPAuthProvider;